/* assets/product-prefill-options.js — PDP + Quick Add prefill (index-id only, no autoselect)
 * Requires: <variant-selects> (preferred) or option groups in the context.
 *
 * URL support (short has precedence):
 *   - ?prefill_idx_id[1]=<id>        → primary (short) key, wins if both are present
 *   - ?prefill_by_index_id[1]=<id>   → fallback (long) key, used only if short is absent for that index
 *
 * Works on:
 *   - PDP (reads window.location.search)
 *   - Quick Add modal (AJAX): captures query from data-product-url and applies when modal content is injected
 */
(function () {
  if (window.__prefillOptionsLoaded) return;
  window.__prefillOptionsLoaded = true;

  // --------------------------------------------------------------------------
  // tiny utils
  // --------------------------------------------------------------------------
  function cssEscape(s){ return String(s||'').replace(/["\\]/g,'\\$&'); }
  function onReady(fn){ document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn) : fn(); }

  // Treat class-based "disabled" as disabled too (some themes mark availability via classes)
  function isDisabledLike(el){
    if (!el) return true;
    if (el.disabled) return true;
    if (String(el.getAttribute('aria-disabled')||'').toLowerCase() === 'true') return true;
    if (el.classList && el.classList.contains('disabled')) return true;

    // Check common wrappers/labels for disabled state
    var lab = el.id ? el.ownerDocument.querySelector('label[for="'+cssEscape(el.id)+'"]') : null;
    if (lab && lab.classList && lab.classList.contains('disabled')) return true;

    var wrap = el.closest && el.closest('.product-form__input, fieldset[aria-label], .variant__option-group, .swatch');
    if (wrap && /\bdisabled\b/i.test(wrap.className||'')) return true;

    return false;
  }

  // Parse both short and long keys; short (prefill_idx_id) wins per index.
  function parseIndexIdMap(qs) {
    var params = typeof qs === 'string' ? new URLSearchParams(qs) : (qs || new URLSearchParams());
    var byIndexId = Object.create(null);
    var any = false;

    // 1) Long key first (fallback layer)
    params.forEach(function (val, key) {
      var m = /^prefill_by_index_id\[(\d+)\]$/.exec(key);
      if (m) {
        var idx0 = (parseInt(m[1],10) - 1);
        byIndexId[idx0] = val;
        any = true;
      }
    });

    // 2) Short key overrides (primary layer)
    params.forEach(function (val, key) {
      var m = /^prefill_idx_id\[(\d+)\]$/.exec(key);
      if (m) {
        var idx0 = (parseInt(m[1],10) - 1);
        byIndexId[idx0] = val; // overwrite fallback if present
        any = true;
      }
    });

    return any ? byIndexId : null;
  }

  function getRoot(ctx){
    // Prefer the <variant-selects> wrapper. If not present, use the given context.
    if (!ctx || !ctx.querySelector) return null;
    return ctx.querySelector('variant-selects') || ctx;
  }

  function findOptionGroups(root){
    // Broad but cheap: common wrappers for option groups across Dawn-like themes
    var nodes = root.querySelectorAll(
      'fieldset.product-form__input,' +               // Dawn radios
      '.product-form__input,' +                       // generic group wrapper
      'fieldset[aria-label],' +                       // a11y-labelled fieldsets
      '.product-form__input--dropdown,' +             // selects
      '.swatch,' +                                    // swatch blocks
      '.variant__option-group'                        // common custom class
    );
    return nodes.length ? Array.prototype.slice.call(nodes) : Array.prototype.slice.call(root.children || []);
  }

  function setOptionIdInGroup(group, valueId){
    if (!group || !valueId) return '';
    var short = String(valueId).split('/').pop();

    // Support <input data-option-value-id>, <option data-option-value-id>, and legacy [data-value-id]
    var sel =
      '[data-option-value-id="'+cssEscape(valueId)+'"],[data-option-value-id="'+cssEscape(short)+'"],' +
      'option[data-option-value-id="'+cssEscape(valueId)+'"],option[data-option-value-id="'+cssEscape(short)+'"],' +
      '[data-value-id="'+cssEscape(valueId)+'"],[data-value-id="'+cssEscape(short)+'"]';

    var el = group.querySelector(sel);
    if (!el) return '';

    // Skip unavailable radio/select-like targets using class/attr based checks
    if (el.tagName === 'INPUT' && el.type === 'radio' && isDisabledLike(el)) return '';

    if (el.tagName === 'OPTION' && el.parentElement) {
      var s = el.parentElement;
      if (isDisabledLike(s)) return '';
      s.value = el.value;
      s.dispatchEvent(new Event('change',{bubbles:true}));
      return (el.textContent || el.value || '').trim();
    }

    // Radio / button
    if (el.type === 'radio' && !el.checked) el.checked = true;
    el.click && el.click();
    el.dispatchEvent && el.dispatchEvent(new Event('change',{bubbles:true}));

    // Best-effort label text
    var label = '';
    if (el.id) {
      var lab = group.querySelector('label[for="'+cssEscape(el.id)+'"]');
      if (lab) label = (lab.textContent || '').replace(/\s*Varianten.*$/, '').trim();
    }
    return label || (el.getAttribute('data-value') || el.value || el.textContent || '').trim();
  }

  function nudgeVariantChange(root){
    var idInput =
      (root.closest && root.closest('product-form, form, .product')?.querySelector('input[name="id"]')) ||
      document.querySelector('input[name="id"]');
    if (idInput) idInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function applyPrefillInto(ctx, qsLike){
    var root = getRoot(ctx);
    if (!root) return false;

    var params = typeof qsLike==='string' ? new URLSearchParams(qsLike) : (qsLike || new URLSearchParams());

    // Index-id prefill only (no autoselect, and we do NOT bail if ?variant= is present)
    var byIndexId = parseIndexIdMap(params);
    if (!byIndexId) return false;

    var groups = findOptionGroups(root);

    // Apply fixed choices by option index → option_value_id
    for (var idx in byIndexId) {
      var i = idx|0;
      setOptionIdInGroup(groups[i], byIndexId[idx]);
    }

    // Nudge the theme's variant logic if a hidden id input exists
    nudgeVariantChange(root);
    return true;
  }

  // --------------------------------------------------------------------------
  // PDP path (run only if relevant prefill params exist)
  // --------------------------------------------------------------------------
  (function prefillPDP(){
    var qs = window.location.search || '';
    if (!/[?&](prefill_idx_id%5B|prefill_idx_id\[|prefill_by_index_id%5B|prefill_by_index_id\[)/.test(qs)) return;
    onReady(function(){ applyPrefillInto(document, qs); });
  })();

  // --------------------------------------------------------------------------
  // Quick Add path
  // --------------------------------------------------------------------------
  // Capture latest opener query from the quick-add button (data-product-url)
  window.__lastPrefillQS = '';
  document.addEventListener('click', function(ev){
    var btn = ev.target && ev.target.closest && ev.target.closest('[data-product-url]');
    if (!btn) return;
    try {
      var u = new URL(btn.getAttribute('data-product-url'), location.origin);
      window.__lastPrefillQS = u.search || '';
    } catch(e){}
  }, true);

  // Observe modal injections and apply prefill (with a one-frame retry for late content)
  var mo = new MutationObserver(function(muts){
    for (var m=0; m<muts.length; m++){
      var rec = muts[m];
      for (var i=0; i<rec.addedNodes.length; i++){
        var n = rec.addedNodes[i];
        if (!(n instanceof HTMLElement)) continue;

        // Typical quick-add wrappers, or anything that contains option groups
        var looksLikeQuickAdd = (n.matches && (n.matches('.quick-add-modal__content') || n.matches('.quick-add-modal') || n.matches('[role="dialog"]')));
        var hasVariantBits = n.querySelector && (n.querySelector('variant-selects') || n.querySelector('.product-form__input') || n.querySelector('fieldset[aria-label]'));
        if (looksLikeQuickAdd || hasVariantBits) {
          var qs = window.__lastPrefillQS;
          if (!qs) {
            var carrier = n.querySelector && (n.querySelector('[data-url]') || n.querySelector('[data-product-url]'));
            if (carrier) {
              try {
                var u = new URL(carrier.getAttribute('data-url') || carrier.getAttribute('data-product-url'), location.origin);
                qs = u.search || '';
              } catch(e){}
            }
          }
          if (qs) {
            var applied = applyPrefillInto(n, qs);
            if (!applied) {
              (window.requestAnimationFrame || setTimeout)(function(){ applyPrefillInto(n, qs); }, 0);
            }
          }
        }
      }
    }
  });
  try { mo.observe(document.documentElement, {childList:true, subtree:true}); } catch(e){}
})();
